import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Info, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  ARCHETYPE_LABEL,
  BASIS_LABEL,
  MATCH_SOURCE_LABEL,
  ROLE_LABEL,
  fmtDate,
  fmtMoney2,
  fmtRate,
  usePayees,
  useBulkAssign,
  useRecommendation,
  type Basis,
  type ContractCoverage,
  type Recommendation,
  type Role,
} from "../api";

/**
 * Add EVERY payee on a contract in one pass.
 *
 * The shape follows the actual complaint: the contract's facts — its type, sign
 * date, first billing date, window start, the P1 call — are properties of the
 * CONTRACT, not of each person being paid. So they are shown once at the top
 * (read-only where the warehouse knows them) and applied to every row. Only the
 * person, their role, their rate and their month window vary per line.
 *
 * The rows arrive pre-filled from the recommendation: the comp plan decides
 * which roles get paid on this contract type, and the warehouse decides who
 * those people are. Anybody HubSpot named whom the plan does not price is shown
 * as an unpriced signal you can add with one click — never silently dropped.
 */

interface RowDraft {
  key: string;
  payeeId: string;
  role: Role;
  mode: "rate" | "fixed";
  rate: string; // percent, e.g. "15"
  fixed: string;
  monthFrom: string;
  monthTo: string;
  /** Plan rate for this role, when the plan prices it — drives the off-plan hint. */
  planRate: number | null;
  matchSource: string | null;
  matchEvidence: string | null;
  warnings: string[];
}

let seq = 0;
const nextKey = () => `row-${++seq}`;

function draftFromRecommendation(r: Recommendation["recommended"][number]): RowDraft {
  return {
    key: nextKey(),
    payeeId: r.payee_id ?? "",
    role: r.role,
    mode: r.fixed_monthly_amount != null ? "fixed" : "rate",
    rate: r.rate_pct != null ? String(round4(r.rate_pct * 100)) : "",
    fixed: r.fixed_monthly_amount != null ? String(r.fixed_monthly_amount) : "",
    monthFrom: String(r.month_from),
    monthTo: String(r.month_to),
    planRate: r.rate_pct,
    matchSource: r.match_source,
    matchEvidence: r.match_evidence,
    warnings: r.warnings,
  };
}

/** Avoid 14.999999999 when a fraction round-trips through percent. */
function round4(n: number): number {
  return Math.round(n * 10_000) / 10_000;
}

export function BulkAssignDialog({
  open,
  onOpenChange,
  contract,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contract: ContractCoverage;
}) {
  const payees = usePayees(open);
  const rec = useRecommendation(contract.contract_id, open);
  const save = useBulkAssign(contract.contract_id);

  const [rows, setRows] = useState<RowDraft[]>([]);
  const [basis, setBasis] = useState<Basis>("billed");
  const [baseline, setBaseline] = useState("");
  const [windowStart, setWindowStart] = useState("");
  const [p1Override, setP1Override] = useState<boolean | null>(null);
  const [scope, setScope] = useState("");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState(false);

  const data = rec.data ?? null;
  const signDate = data?.sign_date ?? contract.contract_signed_at;
  const firstBilling = data?.first_billing_date ?? contract.billing_effective_from;

  // Seed the form from the recommendation exactly once per open, so the user's
  // edits are never clobbered by a background refetch.
  useEffect(() => {
    if (!open) {
      setTouched(false);
      return;
    }
    if (!data || touched) return;
    setRows(data.recommended.map(draftFromRecommendation));
    // The plan's basis for this archetype, already downgraded server-side when
    // a replacement has no computable baseline.
    setBasis((data.recommended[0]?.basis as Basis) ?? "billed");
    setWindowStart(
      data.first_billing_date ? `${data.first_billing_date.slice(0, 7)}-01` : "",
    );
    setP1Override(null);
    setScope("");
    setNotes("");
    setTouched(true);
  }, [open, data, touched]);

  const payeeRows = useMemo(
    () => (payees.data ?? []).filter((p) => p.is_active),
    [payees.data],
  );

  const p1Days = useMemo(() => {
    if (!signDate || !firstBilling) return null;
    return Math.abs(
      Math.round(
        (new Date(firstBilling).getTime() - new Date(signDate).getTime()) / 86_400_000,
      ),
    );
  }, [signDate, firstBilling]);
  const p1Auto = p1Days != null && p1Days <= 7;
  const p1Effective = p1Override ?? p1Auto;

  const patch = (key: string, next: Partial<RowDraft>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...next } : r)));
  };

  const addRow = (preset?: Partial<RowDraft>) => {
    setRows((prev) => [
      ...prev,
      {
        key: nextKey(),
        payeeId: "",
        role: "ae",
        mode: "rate",
        rate: "",
        fixed: "",
        monthFrom: "1",
        monthTo: "12",
        planRate: null,
        matchSource: null,
        matchEvidence: null,
        warnings: [],
        ...preset,
      },
    ]);
  };

  // ── validation ──────────────────────────────────────────────────────────
  const errors = useMemo(() => {
    const out: Record<string, string> = {};
    if (rows.length === 0) out._rows = "Add at least one payee.";
    rows.forEach((r, i) => {
      if (!r.payeeId) out[`${r.key}.payee`] = "Pick a payee";
      if (r.mode === "rate") {
        const v = Number(r.rate);
        if (r.rate === "" || Number.isNaN(v)) out[`${r.key}.rate`] = "Required";
        else if (v < 0) out[`${r.key}.rate`] = "Negative";
        else if (v > 100) out[`${r.key}.rate`] = "Over 100%";
      } else {
        const v = Number(r.fixed);
        if (r.fixed === "" || Number.isNaN(v)) out[`${r.key}.rate`] = "Required";
        else if (v < 0) out[`${r.key}.rate`] = "Negative";
      }
      const from = Number(r.monthFrom);
      const to = Number(r.monthTo);
      if (!Number.isInteger(from) || from < 1 || from > 24)
        out[`${r.key}.months`] = "1–24";
      else if (!Number.isInteger(to) || to < 1 || to > 24)
        out[`${r.key}.months`] = "1–24";
      else if (from > to) out[`${r.key}.months`] = "End before start";
      // Same payee twice with overlapping windows — the backend rejects the
      // whole slate for this, so catch it before the round trip.
      rows.forEach((other, j) => {
        if (j <= i || other.payeeId !== r.payeeId || !r.payeeId) return;
        const of = Number(other.monthFrom);
        const ot = Number(other.monthTo);
        if (from <= ot && of <= to) {
          out[`${other.key}.months`] =
            `Overlaps M${from}–M${to} for the same payee`;
        }
      });
    });
    if (basis === "replacement_delta" && baseline === "") {
      out._baseline = "Baseline MRR is required for the replacement delta basis.";
    }
    return out;
  }, [rows, basis, baseline]);

  const offPlanCount = rows.filter((r) => {
    if (r.mode === "fixed") return r.planRate != null;
    if (r.planRate == null) return false;
    return round4(Number(r.rate) / 100) !== round4(r.planRate);
  }).length;

  const submit = () => {
    save.mutate(
      {
        contract_id: contract.contract_id,
        account_id: data?.account_id ?? contract.account_id,
        quickbooks_id: data?.quickbooks_id ?? null,
        company_name: contract.company_name,
        contract_type_name: contract.contract_type_name,
        contract_archetype: contract.contract_archetype,
        basis,
        replacement_baseline_mrr: baseline !== "" ? Number(baseline) : null,
        item_scope: scope.trim()
          ? scope.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
        window_start: windowStart || null,
        sign_date: signDate,
        first_billing_date: firstBilling,
        includes_p1_override: p1Override,
        notes: notes || null,
        payees: rows.map((r) => ({
          payee_id: r.payeeId,
          role: r.role,
          rate_pct: r.mode === "rate" ? Number(r.rate) / 100 : null,
          fixed_monthly_amount: r.mode === "fixed" ? Number(r.fixed) : null,
          month_from: Number(r.monthFrom),
          month_to: Number(r.monthTo),
        })),
      },
      {
        onSuccess: (created) => {
          const pending = created.filter((a) => a.status === "pending_approval").length;
          toast.success(
            `${created.length} payee${created.length === 1 ? "" : "s"} added`,
            {
              description:
                pending > 0
                  ? `${pending} off-plan rate${pending === 1 ? "" : "s"} need approval before paying.`
                  : undefined,
            },
          );
          onOpenChange(false);
        },
        onError: (e) =>
          toast.error("Couldn't save the slate", {
            description: (e as Error).message,
          }),
      },
    );
  };

  const blocked = Object.keys(errors).length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add payees — {contract.company_name ?? "contract"}</DialogTitle>
          <DialogDescription>
            {contract.contract_type_name ?? "—"}
            {contract.contract_archetype
              ? ` · plan key ${ARCHETYPE_LABEL[contract.contract_archetype]}`
              : " · no plan archetype"}
            {" · "}signed {fmtDate(signDate)} · first bill {fmtDate(firstBilling)}
            {contract.mrr_total != null ? ` · ${fmtMoney2(contract.mrr_total)}/mo` : ""}
          </DialogDescription>
        </DialogHeader>

        {rec.isLoading && <Card className="h-24 animate-pulse" />}

        {rec.error != null && (
          <Alert variant="warning">
            <AlertTitle>Couldn't build a recommendation</AlertTitle>
            <AlertDescription>
              {rec.error instanceof Error ? rec.error.message : String(rec.error)} — you
              can still add payees by hand below.
            </AlertDescription>
          </Alert>
        )}

        {data?.blockers.map((b) => (
          <Alert key={b} variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>The comp plan can't price this contract</AlertTitle>
            <AlertDescription>{b}</AlertDescription>
          </Alert>
        ))}

        {data && data.plan_grid.length > 0 && <PlanPanel rec={data} />}

        {/* ── the contract's facts: entered ONCE, applied to every row ── */}
        <Card className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3">
          <div className="sm:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Applies to every payee below
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ba-basis">Basis</Label>
            <Select value={basis} onValueChange={(v) => setBasis(v as Basis)}>
              <SelectTrigger id="ba-basis">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BASIS_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ba-window">First full billed month (M1)</Label>
            <Input
              id="ba-window"
              type="date"
              value={windowStart}
              onChange={(e) => setWindowStart(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Contract dates</Label>
            <p className="flex h-10 items-center rounded-md border border-input bg-muted/40 px-3 text-xs tabular-nums">
              signed {fmtDate(signDate)} · bill {fmtDate(firstBilling)}
            </p>
          </div>

          {basis === "replacement_delta" && (
            <div className="flex flex-col gap-1.5 sm:col-span-3">
              <Label htmlFor="ba-baseline">Baseline monthly MRR ($)</Label>
              <Input
                id="ba-baseline"
                inputMode="decimal"
                value={baseline}
                onChange={(e) => setBaseline(e.target.value)}
                aria-invalid={!!errors._baseline}
                placeholder="Greater of prior-month billed vs replaced MRR"
              />
              {errors._baseline && (
                <p className="text-xs text-danger">{errors._baseline}</p>
              )}
            </div>
          )}

          {data?.baseline_unavailable && (
            <Alert variant="warning" className="sm:col-span-3">
              <AlertTitle>No computable replacement baseline</AlertTitle>
              <AlertDescription>{data.baseline_unavailable}</AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between gap-3 sm:col-span-3">
            <div>
              <Label htmlFor="ba-p1">P1 stub month (the "13th month")</Label>
              <p className="text-xs text-muted-foreground">
                {p1Days == null
                  ? "Earned when sign date and first billing are within 7 days."
                  : p1Auto
                    ? `Sign → first billing is ${p1Days} day${p1Days === 1 ? "" : "s"} — P1 earned.`
                    : `Sign → first billing is ${p1Days} days (over 7) — P1 not earned.`}
                {p1Override != null && " (admin override)"}
              </p>
            </div>
            <Switch
              id="ba-p1"
              checked={p1Effective}
              onCheckedChange={(v) => setP1Override(v === p1Auto ? null : v)}
            />
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-3">
            <Label htmlFor="ba-scope">Item scope (optional)</Label>
            <Input
              id="ba-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder="e.g. Reputation Management — comma-separated; empty = all subscription items"
            />
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-3">
            <Label htmlFor="ba-notes">Notes</Label>
            <Input
              id="ba-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why these rates / any special terms"
            />
          </div>
        </Card>

        {/* ── the people ── */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">
            Payees{" "}
            <span className="font-normal text-muted-foreground">
              ({rows.length})
            </span>
          </h3>
          {offPlanCount > 0 && (
            <Badge variant="warning">
              {offPlanCount} off-plan — needs approval
            </Badge>
          )}
        </div>

        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Payee</TableHead>
                  <TableHead className="min-w-[150px]">Role</TableHead>
                  <TableHead className="min-w-[130px]">Rate</TableHead>
                  <TableHead className="min-w-[120px]">Months</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                      No payees yet — add one below.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <PayeeRow
                    key={r.key}
                    row={r}
                    payees={payeeRows}
                    errors={errors}
                    onChange={(next) => patch(r.key, next)}
                    onRemove={() =>
                      setRows((prev) => prev.filter((x) => x.key !== r.key))
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => addRow()}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add a payee
          </Button>
          {errors._rows && <p className="text-xs text-danger">{errors._rows}</p>}
        </div>

        {data && data.unpriced_signals.length > 0 && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>
              HubSpot found people the plan doesn't price
            </AlertTitle>
            <AlertDescription>
              <ul className="mt-1 space-y-2">
                {data.unpriced_signals.map((u, i) => (
                  <li key={`${u.role}-${i}`} className="flex flex-wrap items-center gap-2">
                    <span className="text-sm">{u.reason}</span>
                    {u.payee_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          addRow({
                            payeeId: u.payee_id!,
                            role: u.role,
                            matchSource: u.source,
                            matchEvidence: u.evidence,
                          })
                        }
                      >
                        Add {u.payee_name} as {ROLE_LABEL[u.role]}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={blocked || save.isPending}>
            {save.isPending
              ? "Saving…"
              : `Add ${rows.length} payee${rows.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "We need to look at the comp plan and be able to see what's on there." */
function PlanPanel({ rec }: { rec: Recommendation }) {
  return (
    <Card className="p-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold">
          {rec.plan?.name ?? "Comp plan"}
          <span className="ml-2 font-normal text-muted-foreground">
            rates for {rec.contract_type_name ?? "this type"}
          </span>
        </p>
        {rec.plan && (
          <p className="text-xs text-muted-foreground">
            in effect from {fmtDate(rec.plan.effective_from)}
            {rec.plan.effective_to ? ` to ${fmtDate(rec.plan.effective_to)}` : ""}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {rec.plan_grid.map((cell) => {
          const assigned = rec.already_assigned_roles.includes(cell.role);
          return (
            <div
              key={cell.role}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs"
            >
              <span className="font-medium">{ROLE_LABEL[cell.role]}</span>{" "}
              <span className="tabular-nums">{fmtRate(cell.rate_pct)}</span>
              <span className="text-muted-foreground">
                {" "}
                · M1–M{cell.window_months} · {BASIS_LABEL[cell.basis]}
              </span>
              {assigned && (
                <Badge variant="success" className="ml-1.5">
                  assigned
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function PayeeRow({
  row,
  payees,
  errors,
  onChange,
  onRemove,
}: {
  row: RowDraft;
  payees: { id: string; display_name: string }[];
  errors: Record<string, string>;
  onChange: (next: Partial<RowDraft>) => void;
  onRemove: () => void;
}) {
  const offPlan =
    row.planRate != null &&
    (row.mode === "fixed" || round4(Number(row.rate) / 100) !== round4(row.planRate));

  return (
    <TableRow>
      <TableCell>
        <Select value={row.payeeId} onValueChange={(v) => onChange({ payeeId: v })}>
          <SelectTrigger aria-invalid={!!errors[`${row.key}.payee`]} aria-label="Payee">
            <SelectValue placeholder="Pick a payee" />
          </SelectTrigger>
          <SelectContent>
            {payees.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.display_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {row.matchSource && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Sparkles className="h-3 w-3" />
                  {MATCH_SOURCE_LABEL[row.matchSource] ?? row.matchSource}
                </p>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">
                  Suggested from {row.matchEvidence ?? "the warehouse"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {row.warnings.map((w) => (
          <p key={w} className="mt-1 text-[11px] text-warning">
            {w}
          </p>
        ))}
        {errors[`${row.key}.payee`] && (
          <p className="mt-1 text-xs text-danger">{errors[`${row.key}.payee`]}</p>
        )}
      </TableCell>

      <TableCell>
        <Select value={row.role} onValueChange={(v) => onChange({ role: v as Role })}>
          <SelectTrigger aria-label="Role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ROLE_LABEL).map(([k, label]) => (
              <SelectItem key={k} value={k}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-1">
          {row.mode === "rate" ? (
            <>
              <Input
                className="w-16"
                inputMode="decimal"
                value={row.rate}
                onChange={(e) => onChange({ rate: e.target.value })}
                aria-invalid={!!errors[`${row.key}.rate`]}
                aria-label="Rate percent"
                placeholder="15"
              />
              <span className="text-sm text-muted-foreground">%</span>
            </>
          ) : (
            <>
              <span className="text-sm text-muted-foreground">$</span>
              <Input
                className="w-20"
                inputMode="decimal"
                value={row.fixed}
                onChange={(e) => onChange({ fixed: e.target.value })}
                aria-invalid={!!errors[`${row.key}.rate`]}
                aria-label="Fixed monthly amount"
                placeholder="400"
              />
            </>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="px-1.5 text-[11px]"
            onClick={() => onChange({ mode: row.mode === "rate" ? "fixed" : "rate" })}
          >
            {row.mode === "rate" ? "→ $/mo" : "→ %"}
          </Button>
        </div>
        {errors[`${row.key}.rate`] && (
          <p className="mt-1 text-xs text-danger">{errors[`${row.key}.rate`]}</p>
        )}
        {offPlan && row.planRate != null && (
          <p className="mt-1 text-[11px] text-warning">
            Plan says {fmtRate(row.planRate)} — needs approval
          </p>
        )}
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-1">
          <Input
            className="w-14"
            inputMode="numeric"
            value={row.monthFrom}
            onChange={(e) => onChange({ monthFrom: e.target.value })}
            aria-invalid={!!errors[`${row.key}.months`]}
            aria-label="From month"
          />
          <span className="text-sm text-muted-foreground">–</span>
          <Input
            className="w-14"
            inputMode="numeric"
            value={row.monthTo}
            onChange={(e) => onChange({ monthTo: e.target.value })}
            aria-invalid={!!errors[`${row.key}.months`]}
            aria-label="To month"
          />
        </div>
        {errors[`${row.key}.months`] && (
          <p className="mt-1 text-xs text-danger">{errors[`${row.key}.months`]}</p>
        )}
      </TableCell>

      <TableCell>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          aria-label="Remove this payee"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
